import { useRef, useState } from 'react';
import { Paperclip, Mic, Globe, Send } from 'lucide-react';

interface ChatInputProps {
  onSend: (message: string, files?: File[], browse?: boolean) => void;
  loading?: boolean;
}

const ChatInput = ({ onSend, loading }: ChatInputProps) => {
  const [message, setMessage] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

  const [isRecording, setIsRecording] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);

  const [browseEnabled, setBrowseEnabled] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });

      const mediaRecorder = new MediaRecorder(stream);

      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, {
          type: 'audio/webm',
        });

        const audioFile = new File([audioBlob], `voice-${Date.now()}.webm`, {
          type: 'audio/webm',
        });

        setSelectedFiles((prev) => [...prev, audioFile]);

        stream.getTracks().forEach((track) => track.stop());
      };

      mediaRecorder.start();

      mediaRecorderRef.current = mediaRecorder;
      setIsRecording(true);
    } catch (err) {
      console.error(err);
      alert('Microphone permission denied.');
    }
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    setIsRecording(false);
  };

  {
    /*const handleBrowse = () => {
    window.open("https://yourwebsite.com", "_blank");
  };*/
  }

  const handleSend = () => {
    if (!message.trim() && selectedFiles.length === 0) return;

    onSend(message, selectedFiles, browseEnabled);

    setMessage('');
    setSelectedFiles([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="w-full max-w-3xl mx-auto mt-10">
      <div className="border border-gray-200 rounded-2xl shadow-sm bg-white overflow-hidden">
        <div className="px-4 pt-4">
          <textarea
            rows={2}
            value={message}
            maxLength={300}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Summarise the latest..."
            className="w-full resize-none outline-none text-sm placeholder:italic"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
          />
        </div>

        <div className="flex items-center justify-between border-t px-4 py-3">
          <div className="flex items-center gap-6 text-gray-500">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Paperclip size={16} />
              Attach
              <input
                ref={fileInputRef}
                type="file"
                multiple
                className="hidden"
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);

                  if (files.length > 0) {
                    setSelectedFiles(files);
                  }
                }}
              />
            </label>

            <button
              type="button"
              onClick={() => {
                if (isRecording) {
                  stopRecording();
                } else {
                  startRecording();
                }
              }}
              className={`flex items-center gap-2 text-sm ${isRecording ? 'text-red-500' : 'text-gray-500'}`}
            >
              <Mic size={16} />
              {isRecording ? 'Stop' : 'Voice'}
            </button>

            <button
              type="button"
              //onClick={handleBrowse}
              className="flex items-center gap-2 text-sm text-gray-500"
            >
              <Globe size={16} />
              Browse
            </button>
          </div>

          <div className="flex items-center gap-4">
            <span className="text-xs text-gray-400">{message.length}/300</span>

            <button
              type="button"
              disabled={loading}
              onClick={handleSend}
              className="w-9 h-9 flex items-center justify-center rounded-full bg-teal-500 text-white hover:bg-teal-600 disabled:opacity-50"
            >
              <Send size={16} />
            </button>
          </div>
        </div>

        {selectedFiles.length > 0 && (
          <div className="px-4 py-2 text-sm text-gray-500">Attached: {selectedFiles.map((file) => file.name).join(', ')}</div>
        )}
      </div>

      <p className="text-center text-xs text-gray-400 mt-4">Chatbot may create inaccurate data or information on people, places, facts.</p>
    </div>
  );
};

export default ChatInput;
